/**
 * aC Men's Hub の oRPC contract（contract-first）。
 *
 * API（apps/api/ac-mens-hub）はこの contract を `implement` してハンドラを実装し、Web
 * （apps/web/ac-mens-hub）は `ContractRouterClient<typeof acMensHubContract>` として型付き
 * クライアントを生成する。入出力スキーマは本 package の zod schema を SoT として共有するため、
 * API と Web の通信は手書きの型やエンドポイント URL 文字列ではなく、この contract の型から
 * 自動導出される。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business aC Men's Hub の API ↔ Web 通信を oRPC contract-first で型共有し、エンドポイント単位の型ドリフトを排除する。
 * @graph-connects none
 */
import { oc } from '@orpc/contract';
import { KnowledgeMetaDeactivateInputSchema, KnowledgeMetaDeactivateResultSchema, KnowledgeMetaDetailQuerySchema, KnowledgeMetaDetailSchema, KnowledgeMetaIngestInputSchema, KnowledgeMetaIngestResultSchema, KnowledgeMetaSearchQuerySchema, KnowledgeMetaSearchResponseSchema, KnowledgeMetaStateResponseSchema, } from './knowledge-meta.js';
import { KnowledgeSeoDeactivateInputSchema, KnowledgeSeoDeactivateResultSchema, KnowledgeSeoIngestInputSchema, KnowledgeSeoIngestResultSchema, KnowledgeSeoStateResponseSchema, } from './knowledge-seo.js';
import { CoordeCapacityResponseSchema } from './coorde-capacity.js';
import { CoordePreviewRequestSchema, CoordePreviewResponseSchema } from './coorde-outfit.js';
import { CoordeRunAcceptedSchema, CoordeRunListSchema, CoordeRunQuerySchema, CoordeRunRequestSchema, CoordeRunSchema, CoordeSetApprovalRequestSchema, } from './coorde-run.js';
import { IssueCaseLinkInputSchema, IssueConflictDataSchema, IssueCreatedResultSchema, IssueCreateInputSchema, IssueDecideInputSchema, IssueDetailQuerySchema, IssueDetailSchema, IssueDomainsResponseSchema, IssueHistoryQuerySchema, IssueHistoryResponseSchema, IssueListQuerySchema, IssueListResponseSchema, IssueMarkDoneInputSchema, IssueMembersResponseSchema, IssueMergeInputSchema, IssueMutationResultSchema, IssueReopenInputSchema, IssueSettingsSchema, IssueSettingsUpdateInputSchema, IssueThemesResponseSchema, IssueUndoInputSchema, IssueUnmergeInputSchema, IssueUpdateInputSchema, ManualVoiceCreateInputSchema, PointLinkInputSchema, PointUnlinkInputSchema, ThemeCreateInputSchema, ThemeUpdateInputSchema, } from './issue-mgmt.js';
import { BacklogSyncRunResultSchema, BacklogSyncStatusSchema, CaseCandidatesResponseSchema, IssueCaseCheckResultSchema, IssueCasesQuerySchema, IssueCasesResponseSchema, } from './issue-mgmt-backlog.js';
import { IngestChannelAddInputSchema, IngestChannelEnabledInputSchema, IngestChannelRefInputSchema, IngestChannelSchema, IngestInspectionFinishInputSchema, IngestInspectionResponseSchema, IngestInspectionSchema, IngestRunAcceptedSchema, IngestStatusResponseSchema, SlackUrlIngestInputSchema, SlackUrlIngestResultSchema, VoiceRefInputSchema, } from './issue-ingest.js';
import { ExcludedVoicesQuerySchema, ExcludedVoicesResponseSchema, IssueAcknowledgeInputSchema, IssueRefInputSchema, IssueRepliedInputSchema, PointTitleUpdateInputSchema, PointTriageInputSchema, ReviewEventResultSchema, StaleNotifyRunSchema, StaleNotifyStatusSchema, TodoListResponseSchema, } from './issue-todo.js';
import { ItemCatalogResponseSchema } from './item-catalog.js';
import { ProductBacklogListResponseSchema } from './product-backlog.js';
import { ProductBacklogApplyInputSchema, ProductBacklogApplyResultSchema, ProductBacklogDraftResponseSchema, ProductBacklogMovesResponseSchema, ProductBacklogSaveDraftInputSchema, ProductBacklogUndoInputSchema, } from './product-backlog-write.js';
import { RevenueChecksQuerySchema, RevenueChecksResponseSchema, RevenueCloseInputSchema, RevenueClosingSchema, RevenueClosingsQuerySchema, RevenueClosingsResponseSchema, RevenueDefinitionsResponseSchema, RevenueLedgerCompareQuerySchema, RevenueLedgerCompareResponseSchema, RevenueLedgerDailyQuerySchema, RevenueLedgerDailyResponseSchema, RevenueLedgerMonthlyQuerySchema, RevenueLedgerMonthlyResponseSchema, } from './revenue-ledger.js';
import { StylingReviewDetailQuerySchema, StylingReviewDetailSchema, StylingReviewListQuerySchema, StylingReviewListResponseSchema, StylingReviewMarkRequestSchema, StylingReviewMarkSchema, } from './styling-review.js';
/**
 * aC Men's Hub API が返す代表的なエラー集合を持つ contract builder。
 * @graph-connects none
 */
const base = oc.errors({
    UNAUTHORIZED: { message: 'ログインが必要です' },
    FORBIDDEN: { message: 'アクセス権限がありません' },
    INTERNAL_SERVER_ERROR: { message: '内部エラーが発生しました' },
});
/**
 * item-catalog feature の oRPC contract（list）。
 * 母数が 651 件程度と小さいため入力（絞り込み・ページング）を取らず全件返し、絞り込みは画面側で行う。
 * @graph-connects none
 */
export const itemCatalogContract = {
    list: base.output(ItemCatalogResponseSchema),
};
/**
 * coorde feature の oRPC contract。
 * - `capacity`: 季節は6区分しかなく、画面は全季節を並べて比較するため入力を取らず全季節を返す
 * - `preview`: 画像生成は従量課金のため、**何が作られるかを先に返す**。生成はまだ行わない
 * - `requestRun`: 生成を受け付けてランを作る。画像生成は非同期なのでこの時点では完了していない
 * - `getRun`: ランの進捗と結果を返す。画面はこれをポーリングする
 * - `setApproval`: 生成物1本に人の判定を記録する。判定後のラン全体を返し、画面の再取得を省く
 * - `listRuns`: 履歴一覧。件数だけに畳んだ要約を新しい順で返す
 * @graph-connects none
 */
export const coordeContract = {
    capacity: base.output(CoordeCapacityResponseSchema),
    preview: base.input(CoordePreviewRequestSchema).output(CoordePreviewResponseSchema),
    requestRun: base.input(CoordeRunRequestSchema).output(CoordeRunAcceptedSchema),
    getRun: base.input(CoordeRunQuerySchema).output(CoordeRunSchema),
    setApproval: base.input(CoordeSetApprovalRequestSchema).output(CoordeRunSchema),
    listRuns: base.output(CoordeRunListSchema),
};
/**
 * styling-review feature の oRPC contract。
 * - `list`: 一覧。既定は未確認のみで、毎朝の「残り何件」を件数付きで返す
 * - `detail`: 1件を判断するのに要る情報を1応答にまとめる（人・要望・前回まで・送った服・お客様の答え）
 * - `mark`: 見立てと既読を保存する。見立てを決めずに読了だけ記録する経路も同じ手続きで通す
 * @graph-connects none
 */
export const stylingReviewContract = {
    list: base.input(StylingReviewListQuerySchema).output(StylingReviewListResponseSchema),
    detail: base
        .errors({ NOT_FOUND: { message: '選定が見つかりません' } })
        .input(StylingReviewDetailQuerySchema)
        .output(StylingReviewDetailSchema),
    mark: base.input(StylingReviewMarkRequestSchema).output(StylingReviewMarkSchema),
};
/**
 * knowledge-meta feature の oRPC contract。
 * - `search`: キーワード検索。ヒット箇所の抜粋と種類別の件数を返す（件数だけの一覧にしない）
 * - `detail`: 記事の全文。出典URLを必ず伴う
 * - `state`: 収録済み全件の doc_id と内容ハッシュ。母艦が差分を出すために使う
 * - `ingest`: 記事の投入。変わったものだけを送る前提で件数を制限する
 * - `deactivate`: Meta から消えた記事を検索対象から外す。物理削除はしない
 *
 * 取り込み系も同じ `/rpc/knowledgeMeta/*` 配下に置くが、**書き込みは閲覧と別の権限**にする。
 * `search` / `detail` / `state` は `KNOWLEDGE_META_PERMISSION`（閲覧）、`ingest` / `deactivate`
 * はそれに加えて `KNOWLEDGE_META_WRITE_PERMISSION` を要求する。一次情報の写しなので、
 * 投入・無効化ができることは改竄ができることと同義で、閲覧できる相手全員に配ってはならない。
 *
 * Hub の権限キーはナビ項目から導出される（`ALL_PAGE_KEYS`）が、`extraPermissions` に載せれば
 * ナビへ独立した項目を出さずに権限管理画面から付与できる（`member-staging` と同じ扱い）。
 * `state` を閲覧側に残すのは、本文を返さない読み取りだから（差分判定に使う）。
 * @graph-connects none
 */
export const knowledgeMetaContract = {
    search: base.input(KnowledgeMetaSearchQuerySchema).output(KnowledgeMetaSearchResponseSchema),
    detail: base
        .errors({ NOT_FOUND: { message: '記事が見つかりません' } })
        .input(KnowledgeMetaDetailQuerySchema)
        .output(KnowledgeMetaDetailSchema),
    state: base.output(KnowledgeMetaStateResponseSchema),
    ingest: base.input(KnowledgeMetaIngestInputSchema).output(KnowledgeMetaIngestResultSchema),
    deactivate: base
        .input(KnowledgeMetaDeactivateInputSchema)
        .output(KnowledgeMetaDeactivateResultSchema),
};
/**
 * knowledge-seo feature の oRPC contract。
 * - `state`: 収録済み全件の doc_id と内容ハッシュ。母艦が差分を出すために使う
 * - `ingest`: 記事の投入。変わったものだけを送る前提で件数を制限する
 * - `deactivate`: 出典から消えた記事を対象外にする。物理削除はしない
 *
 * 画面を持たないため検索・全文取得は用意しない。蓄積したものは BigQuery から直接引く。
 * `state` を閲覧側に置くのは、本文を返さない読み取りだから（差分判定に使う）。
 * `ingest` / `deactivate` は `KNOWLEDGE_SEO_PERMISSION` に加えて
 * `KNOWLEDGE_SEO_WRITE_PERMISSION` を要求する。一次情報の写しなので、投入・無効化が
 * できることは改竄ができることと同義で、読める相手全員に配ってはならない。
 * @graph-connects none
 */
export const knowledgeSeoContract = {
    state: base.output(KnowledgeSeoStateResponseSchema),
    ingest: base.input(KnowledgeSeoIngestInputSchema).output(KnowledgeSeoIngestResultSchema),
    deactivate: base
        .input(KnowledgeSeoDeactivateInputSchema)
        .output(KnowledgeSeoDeactivateResultSchema),
};
/**
 * revenue-ledger feature（売上集計の定義の版つき台帳）の oRPC contract。
 * - `monthly`: 版と月の範囲で、月×勘定・プラン・内訳を返す（締め済み月は締めた計算の値）。run は版全体の最新の計算と最初の計上月（版の台帳が空なら null）
 * - `daily`: 正式版の日次。当月累計の前日からの増加。台帳が始まる前の日は日別内訳（取引日で数え直した参考値）を使い、その最後の日を referenceThrough で返す。firstDate（日別内訳と台帳の最初の日の早い方）より前の行は返さず、firstDate 以降の月は日次を月で足すと月次に一致する
 * - `compare`: 2つの版を対応表の行ごとに並べ、値・差額・注記を返す（締め済み月は締めた計算の値）。当月の値をまだ出さない月を pendingMonths で返し、両版ともその月を除いて比べる
 * - `checks`: 最新の計算の検算・検知の違反と、締め後の差を要対応として返す
 * - `definitions`: 定義の版の一覧と本文
 * - `closings`: 月ごとの締めの状態
 * - `close`: 月を締める・締め直す。押す前に見ていた確定値と違えば CONFLICT で上書きせず、月末を過ぎていない月は BAD_REQUEST
 *
 * 閲覧は売上ページの権限（`revenue`）、`close` はそれに加えて `REVENUE_CLOSE_PERMISSION` を要求する。
 * @graph-connects none
 */
export const revenueLedgerContract = {
    monthly: base.input(RevenueLedgerMonthlyQuerySchema).output(RevenueLedgerMonthlyResponseSchema),
    daily: base.input(RevenueLedgerDailyQuerySchema).output(RevenueLedgerDailyResponseSchema),
    compare: base.input(RevenueLedgerCompareQuerySchema).output(RevenueLedgerCompareResponseSchema),
    checks: base.input(RevenueChecksQuerySchema).output(RevenueChecksResponseSchema),
    definitions: base.output(RevenueDefinitionsResponseSchema),
    closings: base.input(RevenueClosingsQuerySchema).output(RevenueClosingsResponseSchema),
    close: base
        .errors({
        CONFLICT: { message: '他の人が先に締めたか、計算が更新されました' },
        NOT_FOUND: { message: 'この月の計算が台帳にありません' },
        BAD_REQUEST: { message: '月末を過ぎていない月は締められません' },
    })
        .input(RevenueCloseInputSchema)
        .output(RevenueClosingSchema),
};
/**
 * issue-mgmt feature（課題管理の台帳）の閲覧の oRPC contract。
 * - `list`: 課題の一覧と検索。検索したときは課題に紐づいていない声も返す
 * - `detail`: 課題1件の詳細（声・案件の紐づけ・テーマ・統合）
 * - `history`: 課題1件の変更履歴（課題と、今紐づく論点・案件の紐づけ、外れた論点）
 * - `members`: 担当の候補（編集キーを持つ人）
 * - `themes`: テーマの一覧
 * - `settings`: 課題管理の設定（当番・判定者・週次の曜日・AI のおすすめ）
 * - `domains`: 領域の選択肢（台帳の課題が使っている領域）
 * - `cases`: 課題1件の案件欄（紐づく Backlog 案件の写し・進捗・未完了の件数）
 * - `backlogSync`: Backlog 同期の状況（ミラーの鮮度・実行中か・最後の実行）
 * - `caseCandidates`: 案件を紐づける小窓の候補（未対応のメンズの親課題、作成日の新しい順）
 * - `staleNotify`: 滞留通知の状況（最後の実行と結果・前回送れなかった時刻）
 *
 * `ISSUE_MGMT_PERMISSION` を要求する。書き込みは別の namespace（`issueMgmtWrite`）に分け、
 * その配下全体に編集の権限を重ねる（手続きを足しても権限の付け忘れが起きない）。
 * @graph-connects none
 */
export const issueMgmtContract = {
    list: base.input(IssueListQuerySchema).output(IssueListResponseSchema),
    detail: base
        .errors({ NOT_FOUND: { message: '課題が見つかりません' } })
        .input(IssueDetailQuerySchema)
        .output(IssueDetailSchema),
    history: base.input(IssueHistoryQuerySchema).output(IssueHistoryResponseSchema),
    members: base.output(IssueMembersResponseSchema),
    themes: base.output(IssueThemesResponseSchema),
    settings: base.output(IssueSettingsSchema),
    domains: base.output(IssueDomainsResponseSchema),
    cases: base
        .errors({ NOT_FOUND: { message: '課題が見つかりません' } })
        .input(IssueCasesQuerySchema)
        .output(IssueCasesResponseSchema),
    backlogSync: base.output(BacklogSyncStatusSchema),
    caseCandidates: base.output(CaseCandidatesResponseSchema),
    staleNotify: base.output(StaleNotifyStatusSchema),
};
/**
 * issue-mgmt feature の「やること」（振り分け・判定・確認のカード）の閲覧の oRPC contract。
 * - `cards`: やることのカードの一覧（並び順どおり）と週次の案内。自分が受け持つカードには mine が付く
 * - `excluded`: AI が除外した投稿（直近30日・新しい順）
 *
 * やることは編集キーの人だけの画面なので、`ISSUE_MGMT_PERMISSION` と `ISSUE_MGMT_WRITE_PERMISSION` の両方を要求する。
 * @graph-connects none
 */
export const issueTodoContract = {
    cards: base.output(TodoListResponseSchema),
    excluded: base.input(ExcludedVoicesQuerySchema).output(ExcludedVoicesResponseSchema),
};
/**
 * 課題管理の書き込みが返しうるエラー。
 * - CONFLICT: 画面を開いた後に他の人が同じ項目を変えた。data に項目・今の値・変えた人を載せる
 * - NOT_FOUND: 対象の課題・論点・テーマ・操作が無い
 * - BAD_REQUEST: 業務ルールに合わない（必須項目の欠け・選べない日付・統合済みへの操作など）
 * @graph-connects none
 */
const issueMgmtWriteBase = base.errors({
    CONFLICT: {
        message: '他の人が先に変更しました。再読み込みしてください',
        data: IssueConflictDataSchema,
    },
    NOT_FOUND: { message: '対象が見つかりません' },
    BAD_REQUEST: { message: '保存できない内容です' },
});
/**
 * issue-mgmt feature（課題管理の台帳）の書き込みの oRPC contract。
 * どの手続きも、確定した保存操作だけを変更履歴（BigQuery `issue__change_logs`）へ書く。書けなかった分は後から送り直す。
 * 返す operationId を `undo` に渡すと、その保存を打ち消す変更を新しく保存する（履歴は消さない）。
 * - `checkCase`: 要確認・期限超過の案件に「Backlogで確かめた」を付ける（見つからない・削除なら付けず、次の同期で取り直す）
 * - `syncBacklog`: Backlog 同期を今すぐ1回動かし、終わるまで待って結果を返す（実行中なら始めない）
 * - `triagePoint`・`updatePointTitle`・`returnVoiceToTriage`: 振り分けの「課題ではない」「あとで」・声の要点を直す・
 *   課題ではない／旧表で処理済みの声を振り分けに戻す
 * - `acknowledge`・`markReplied`・`confirmFixed`・`markNotFixed`・`markInProgress`: 判定・確認のカードの
 *   「このままでよい」「返信済みにする」「直った」「まだ直っていない」「まだ対応中」
 * - `recordReviewOpen`: 週次の日に判定のカードを開いたことを記録する（所要時間の記録）
 * - `notifyStaleNow`: 滞留通知を今すぐ1回送る
 *
 * `ISSUE_MGMT_PERMISSION` に加えて `ISSUE_MGMT_WRITE_PERMISSION` を要求する。
 * @graph-connects none
 */
export const issueMgmtWriteContract = {
    createIssue: issueMgmtWriteBase.input(IssueCreateInputSchema).output(IssueCreatedResultSchema),
    updateIssue: issueMgmtWriteBase.input(IssueUpdateInputSchema).output(IssueMutationResultSchema),
    decide: issueMgmtWriteBase.input(IssueDecideInputSchema).output(IssueMutationResultSchema),
    merge: issueMgmtWriteBase.input(IssueMergeInputSchema).output(IssueMutationResultSchema),
    unmerge: issueMgmtWriteBase.input(IssueUnmergeInputSchema).output(IssueMutationResultSchema),
    linkCase: issueMgmtWriteBase.input(IssueCaseLinkInputSchema).output(IssueMutationResultSchema),
    unlinkCase: issueMgmtWriteBase.input(IssueCaseLinkInputSchema).output(IssueMutationResultSchema),
    linkPoint: issueMgmtWriteBase.input(PointLinkInputSchema).output(IssueMutationResultSchema),
    unlinkPoint: issueMgmtWriteBase.input(PointUnlinkInputSchema).output(IssueMutationResultSchema),
    createVoice: issueMgmtWriteBase
        .input(ManualVoiceCreateInputSchema)
        .output(IssueCreatedResultSchema),
    createTheme: issueMgmtWriteBase.input(ThemeCreateInputSchema).output(IssueCreatedResultSchema),
    updateTheme: issueMgmtWriteBase.input(ThemeUpdateInputSchema).output(IssueMutationResultSchema),
    undo: issueMgmtWriteBase.input(IssueUndoInputSchema).output(IssueMutationResultSchema),
    markDone: issueMgmtWriteBase.input(IssueMarkDoneInputSchema).output(IssueMutationResultSchema),
    reopen: issueMgmtWriteBase.input(IssueReopenInputSchema).output(IssueMutationResultSchema),
    updateSettings: issueMgmtWriteBase
        .input(IssueSettingsUpdateInputSchema)
        .output(IssueSettingsSchema),
    checkCase: issueMgmtWriteBase.input(IssueCaseLinkInputSchema).output(IssueCaseCheckResultSchema),
    syncBacklog: issueMgmtWriteBase.output(BacklogSyncRunResultSchema),
    triagePoint: issueMgmtWriteBase.input(PointTriageInputSchema).output(IssueMutationResultSchema),
    updatePointTitle: issueMgmtWriteBase
        .input(PointTitleUpdateInputSchema)
        .output(IssueMutationResultSchema),
    returnVoiceToTriage: issueMgmtWriteBase
        .input(VoiceRefInputSchema)
        .output(IssueMutationResultSchema),
    acknowledge: issueMgmtWriteBase
        .input(IssueAcknowledgeInputSchema)
        .output(IssueMutationResultSchema),
    markReplied: issueMgmtWriteBase.input(IssueRepliedInputSchema).output(IssueMutationResultSchema),
    confirmFixed: issueMgmtWriteBase.input(IssueRefInputSchema).output(IssueMutationResultSchema),
    markNotFixed: issueMgmtWriteBase.input(IssueRefInputSchema).output(IssueMutationResultSchema),
    markInProgress: issueMgmtWriteBase.input(IssueRefInputSchema).output(IssueMutationResultSchema),
    recordReviewOpen: issueMgmtWriteBase.input(IssueRefInputSchema).output(ReviewEventResultSchema),
    notifyStaleNow: issueMgmtWriteBase.output(StaleNotifyRunSchema),
};
/**
 * issue-mgmt feature の取込み（Slack の投稿を声にする処理・チャンネル・本文削除・点検）の閲覧の oRPC contract。
 * - `status`: 取込みチャンネルの一覧と状態・最後の取込みの実行・前回の点検
 * - `inspection`: 最新の点検（抜き取った除外と声の行）
 *
 * 台帳（`issueMgmt`）と同じく `ISSUE_MGMT_PERMISSION` を要求する。
 * @graph-connects none
 */
export const issueIngestContract = {
    status: base.output(IngestStatusResponseSchema),
    inspection: base.output(IngestInspectionResponseSchema),
};
/**
 * issue-mgmt feature の取込みの書き込みの oRPC contract。
 * - `runNow`: 今すぐ取り込む（裏で実行し、実行 ID をすぐ返す。実行中なら CONFLICT）
 * - `ingestSlackUrl`: Slack の投稿 URL を貼って声にする（取込み対象のチャンネル以外でも）
 * - `addChannel`・`setChannelEnabled`・`markChannelInvited`: 取込みチャンネルを足す・止める／再開する・「招待した」
 * - `deleteVoiceBody`: 伏せ字漏れの本文削除（声の本文と論点の文を消し、紐づけは残す）
 * - `restoreExcludedVoice`: AI が除外した投稿を振り分けに戻す
 * - `startInspection`・`finishInspection`: 月1回の点検を始める・終える
 *
 * 台帳の書き込み（`issueMgmtWrite`）と同じく `ISSUE_MGMT_PERMISSION` と `ISSUE_MGMT_WRITE_PERMISSION` を要求する。
 * @graph-connects none
 */
export const issueIngestWriteContract = {
    runNow: issueMgmtWriteBase.output(IngestRunAcceptedSchema),
    ingestSlackUrl: issueMgmtWriteBase
        .input(SlackUrlIngestInputSchema)
        .output(SlackUrlIngestResultSchema),
    addChannel: issueMgmtWriteBase.input(IngestChannelAddInputSchema).output(IngestChannelSchema),
    setChannelEnabled: issueMgmtWriteBase
        .input(IngestChannelEnabledInputSchema)
        .output(IngestChannelSchema),
    markChannelInvited: issueMgmtWriteBase
        .input(IngestChannelRefInputSchema)
        .output(IngestChannelSchema),
    deleteVoiceBody: issueMgmtWriteBase.input(VoiceRefInputSchema).output(IssueMutationResultSchema),
    restoreExcludedVoice: issueMgmtWriteBase
        .input(VoiceRefInputSchema)
        .output(IssueMutationResultSchema),
    startInspection: issueMgmtWriteBase.output(IngestInspectionSchema),
    finishInspection: issueMgmtWriteBase
        .input(IngestInspectionFinishInputSchema)
        .output(IngestInspectionSchema),
};
/**
 * product-backlog feature（メンズの案件一覧）の閲覧の oRPC contract。
 * - `list`: 開発タブ「開発案件一覧_メンズ」を読み、区分ごとの案件・形の問題・書き写しのずれ・Backlog の値を返す
 * - `moves`: 動かした記録（新しい順の50件）と、元に戻せる直前の反映
 *
 * `PRODUCT_BACKLOG_PERMISSION` を要求する。書き込む手続きは別の namespace（`productBacklogWrite`）に置き、
 * その配下全体に `PRODUCT_BACKLOG_WRITE_PERMISSION` を重ねる（app.ts）。
 * @graph-connects none
 */
export const productBacklogContract = {
    list: base.output(ProductBacklogListResponseSchema),
    moves: base.output(ProductBacklogMovesResponseSchema),
};
/**
 * product-backlog feature（メンズの案件）の書き込みの oRPC contract。
 * - `draft` / `saveDraft` / `discardDraft`: 自分の並べ替えの下書きを読む・保存する・捨てる（スプシは変わらない）
 * - `apply`: 下書きを全社スプシへ反映する（行の移動だけを1回の一括で書き、読み戻して確かめる）
 * - `undo`: 直前の反映を元に戻す（同じ確かめ方で反映前の並びへ動かす）
 *
 * 反映できなかった理由（時間帯・並びの変化・ずれ・鍵・権限・障害）はエラーではなく結果の outcome で返す。
 * 下書きの形が決まりに合わない（動かせない区分への移動など）ときだけ BAD_REQUEST。
 * `PRODUCT_BACKLOG_PERMISSION` に加えて `PRODUCT_BACKLOG_WRITE_PERMISSION` を要求する（app.ts）。
 * @graph-connects none
 */
export const productBacklogWriteContract = {
    draft: base.output(ProductBacklogDraftResponseSchema),
    saveDraft: base
        .errors({ BAD_REQUEST: { message: '動かせない並べ替えです' } })
        .input(ProductBacklogSaveDraftInputSchema)
        .output(ProductBacklogDraftResponseSchema),
    discardDraft: base.output(ProductBacklogDraftResponseSchema),
    apply: base.input(ProductBacklogApplyInputSchema).output(ProductBacklogApplyResultSchema),
    undo: base.input(ProductBacklogUndoInputSchema).output(ProductBacklogApplyResultSchema),
};
/**
 * aC Men's Hub の oRPC contract ルート。
 * 手続きは `/rpc/<namespace>/<procedure>` に対応する（例: `itemCatalog.list` → `/rpc/itemCatalog/list`）。
 * @graph-connects none
 */
export const acMensHubContract = {
    itemCatalog: itemCatalogContract,
    coorde: coordeContract,
    stylingReview: stylingReviewContract,
    knowledgeMeta: knowledgeMetaContract,
    knowledgeSeo: knowledgeSeoContract,
    revenueLedger: revenueLedgerContract,
    issueMgmt: issueMgmtContract,
    issueMgmtWrite: issueMgmtWriteContract,
    issueTodo: issueTodoContract,
    issueIngest: issueIngestContract,
    issueIngestWrite: issueIngestWriteContract,
    productBacklog: productBacklogContract,
    productBacklogWrite: productBacklogWriteContract,
};
//# sourceMappingURL=orpc-contract.js.map